import React, {useEffect, useRef, useState, useCallback} from 'react'
import PropTypes from 'prop-types'
import {
    faEye,
    faCode,
    faTree,
    faFileUpload,
    faSitemap,
    faBan,
    faTimes
} from '@fortawesome/free-solid-svg-icons'
import JSONEditor from 'jsoneditor'

import 'jsoneditor/dist/jsoneditor.css'
import './darktheme.css'

import 'ace-builds/webpack-resolver'

import styles from './JsonEditor.module.scss'
import ToolBar from '../../../UI/ToolBar/ToolBar';
import UploadFile from '../../../UI/ToolBar/UploadFile';
import Buttons from '../../../UI/ToolBar/Buttons';

// Advanced JSON editing (Tree/Form/View modes and JSON-Schema upload/validation)
// via the jsoneditor library, kept alongside the shared CodeMirror-based editor
// used for Markdown/YAML/XML in the Editor page. This component is controlled
// (value/onChange) so it shares state with whichever other view is editing the
// same JSON, instead of keeping its own separate copy in local storage.
const JsonEditor = ({value, onChange, onClose, sizeOptions, onSetSize}) => {
    let [schema, setSchema] = useState()
    let [mode, setMode] = useState('code')
    let [active, setActive] = useState('')
    let editorRef = useRef()
    let instanceRef = useRef()
    let timerRef = useRef()
    let lastEmittedRef = useRef(value)

    const emitChange = useCallback(() => {
        if (!instanceRef.current) return
        if (timerRef.current) clearTimeout(timerRef.current)

        timerRef.current = setTimeout(() => {
            if (active === 'show-schema') return
            let newValue
            if (mode === 'tree' || mode === 'form' || mode === 'view') {
                newValue = JSON.stringify(instanceRef.current.get())
            } else {
                newValue = instanceRef.current.getText()
            }
            lastEmittedRef.current = newValue
            onChange(newValue)
        }, 1000)
    }, [mode, active, onChange])

    useEffect(() => {
        let instance = new JSONEditor(editorRef.current, {onChange: emitChange, mode: 'code'})
        instance.setText(value || '{}')
        instanceRef.current = instance

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current)
            instance.destroy()
            instanceRef.current = null
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Keeps the editor in sync when the value changes from outside this
    // component (Sample/Reset/Format in the shared Editor toolbar, or an edit
    // made in the plain code view) without recreating the editor instance and
    // losing cursor position/undo history on every keystroke.
    useEffect(() => {
        if (!instanceRef.current) return
        if (active === 'show-schema') return
        if (value === lastEmittedRef.current) return
        try {
            instanceRef.current.setText(value || '{}')
            lastEmittedRef.current = value
        } catch (e) {
            // Leave the editor showing what the user typed; jsoneditor surfaces its own error state.
        }
    }, [value, active])

    const selectMode = (mode) => {
        try {
            instanceRef.current.setMode(mode)
            setMode(mode)
            if (active !== 'show-schema') {
                if (schema) instanceRef.current.setSchema(schema.data)
                instanceRef.current.setText(value || '{}')
            }
        } catch (e) {
            alert(e.message)
        }
    }

    const showSchemaFile = () => {
        if (!schema) {
            return
        }
        if (active === 'show-schema') {
            setActive('')
            instanceRef.current.setMode('code')
            setMode('code')
            instanceRef.current.setText(value || '{}')
        } else {
            instanceRef.current.setMode('view')
            setMode('view')
            instanceRef.current.setText(JSON.stringify(schema.data))
            setActive('show-schema')
        }
    }

    const handleResetSchema = () => {
        instanceRef.current.setSchema()
        setSchema()
    }

    const handleUploadSchema = event => {
        let file = event.target.files[0]
        let fileReader = new FileReader()
        fileReader.onloadend = () => {
            try {
                let value = JSON.parse(fileReader.result)
                instanceRef.current.setSchema(value)
                setSchema({
                    name: file.name,
                    data: value
                })
            } catch (e) {
                console.log('Unable to upload schema file', e)
            }
        }
        fileReader.readAsText(file)
    }

    const sizeButtons = (sizeOptions && onSetSize) ? sizeOptions.map(({mode, tip, icon, active}) => ({
        tip,
        icon,
        active,
        clickHandler: () => onSetSize(mode),
    })) : []

    const closeButton = onClose ? [
        {
            'tip': 'Close advanced view',
            'clickHandler': onClose,
            'icon': faTimes,
        },
    ] : []

    const jsonActionButtons =
        [
            {
                'tip': 'Code edit',
                'clickHandler': selectMode.bind(this, 'code'),
                'icon': faCode,
            },
            {
                'tip': 'Tree edit',
                'clickHandler': selectMode.bind(this, 'tree'),
                'icon': faTree
            },
            {
                'tip': 'Read only',
                'clickHandler': selectMode.bind(this, 'view'),
                'icon': faEye
            }
        ]

    const schemaActionButtons =
        [
            {
                'tip': 'Reset schema file',
                'clickHandler': handleResetSchema,
                'icon': faBan
            },
            {
                'tip': 'View schema',
                'clickHandler': showSchemaFile,
                'icon': faSitemap
            }
        ]

    let schemaText = schema ? <div> Schema file: {schema.name} </div> : ''
    return (
        <div className={styles.jsonEditor}>
            <ToolBar>
                <div style={{display: "flex", justifyContent: "space-between", width: "100%"}}>
                    <div style={{display: "flex"}}>
                        <Buttons buttons={jsonActionButtons}/>
                    </div>
                    <div style={{display: "flex", alignItems: "center"}}>{schemaText}</div>
                    <div style={{display: "flex"}}>
                        <UploadFile id={'file-input-schema'} icon={faFileUpload} handler={handleUploadSchema}
                                    tip={'Upload JSON Schema file'}/>
                        <Buttons buttons={schemaActionButtons}/>
                        <Buttons buttons={sizeButtons}/>
                        <Buttons buttons={closeButton}/>
                    </div>
                </div>
            </ToolBar>
            <div className={styles.editor} ref={editorRef}/>
        </div>
    )
}

JsonEditor.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onClose: PropTypes.func,
    sizeOptions: PropTypes.arrayOf(PropTypes.shape({
        mode: PropTypes.string.isRequired,
        tip: PropTypes.string.isRequired,
        icon: PropTypes.object.isRequired,
        active: PropTypes.bool,
    })),
    onSetSize: PropTypes.func,
}

export default JsonEditor
